import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Grid,
  TextField,
  Box,
  Skeleton,
  Button,
  MenuItem,
} from '@mui/material';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import ProfileUploadContainer from '../../containers/common/fileupload/ProfileUploadContainer';
import HrGradeSelectbox from '../common/form/selectbox/HrGradeSelectbox';
import HrDptSelectbox from '../common/form/selectbox/HrDptSelectbox';
import * as fileApi from '../../lib/api/file';

const Img = styled('img')({
  margin: 'auto',
  display: 'block',
  maxWidth: '100%',
  maxHeight: '100%',
});

const ProfileForm = ({ user, formik }) => {
  const [profileImg, setProfileImg] = useState('');

  useEffect(() => {    
    const loadProfileImage = async () => {
      try {        
        setProfileImg(await fileApi.profileImage(user.profileImg));
      } catch (error) {
        console.error("이미지 호출 에러:", error);
      }
    };

    loadProfileImage();

    return () => {
      if (profileImg) URL.revokeObjectURL(profileImg);
    };
  }, [user?.profileImg]);

  return (
    <Box
      component="form"
      sx={{ mt: 1, p: 2 }}
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
    >
      {/* 메인 전체 레이아웃 (좌측 사진 / 우측 폼) */}
      <Grid container spacing={2}>
        
        {/* [좌측] 프로필 이미지 영역 */}
        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 200,
              height: 170,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              overflow: 'hidden',
              borderRadius: 1,
              border: user.profileImg ? 'none' : '1px solid #e0e0e0'
            }}
          >
            {user.profileImg ? (
              <Img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                alt={user.usrNm}
                src={profileImg}
                referrerPolicy="no-referrer-when-downgrade" 
              />
            ) : (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                animation={false}
              />
            )}
          </Box>
          <ProfileUploadContainer
            fileTyp="profile"
            fileAttrVal1={user.usrUuid}
          />
        </Grid>

        {/* [우측] 폼 입력 필드 영역 */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={1.5} alignItems="center">
            
            {/* ----------------- 첫 번째 줄 (4개 열: 각 xs={3}) ----------------- */}
            <Grid item xs={3}>
              <TextField
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="usrNo"
                name="usrNo"
                label="사번"
                value={formik.values.usrNo}
                onChange={formik.handleChange}
                error={formik.touched.usrNo && Boolean(formik.errors.usrNo)}
                helperText={formik.touched.usrNo && formik.errors.usrNo}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="usrNm"
                name="usrNm"
                label="이름"
                value={formik.values.usrNm}
                onChange={formik.handleChange}
                error={formik.touched.usrNm && Boolean(formik.errors.usrNm)}
                helperText={formik.touched.usrNm && formik.errors.usrNm}
              />
            </Grid>
            <Grid item xs={3}>
              <HrDptSelectbox
                formik={formik}
                name="dptUuid"
                label="부서"
                margin="dense"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={3}>
              <HrGradeSelectbox
                formik={formik}
                name="gradeUuid"
                label="직급"
                margin="dense"
                variant="outlined"
              />
            </Grid>

            {/* ----------------- 두 번째 줄 (4개 열: 각 xs={3}) ----------------- */}
            <Grid item xs={3}>
              <TextField
                select
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="gender"
                name="gender"
                label="성별"
                value={formik.values.gender || 'F'}
                onChange={(event) => formik.setFieldValue('gender', event.target.value)}
                error={formik.touched.gender && Boolean(formik.errors.gender)}
                helperText={formik.touched.gender && formik.errors.gender}
              >
                <MenuItem value="F">여성</MenuItem>
                <MenuItem value="M">남성</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="telNo"
                name="telNo"
                label="연락처"
                value={formik.values.telNo}
                onChange={formik.handleChange}
                error={formik.touched.telNo && Boolean(formik.errors.telNo)}
                helperText={formik.touched.telNo && formik.errors.telNo}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="emlAddr"
                name="emlAddr"
                label="이메일"
                value={formik.values.emlAddr}
                onChange={formik.handleChange}
                error={formik.touched.emlAddr && Boolean(formik.errors.emlAddr)}
                helperText={formik.touched.emlAddr && formik.errors.emlAddr}
              />
            </Grid>
            <Grid item xs={3}>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <DatePicker
                  label="입사일"
                  inputFormat={'yyyy-MM-dd'}
                  mask={'____-__-__'}
                  id="entryDt"
                  name="entryDt"
                  value={formik.values.entryDt || new Date()}
                  onChange={(newValue) => {
                    formik.setFieldValue('entryDt', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* ----------------- 세 번째 줄 (1:3 비율 배치 -> xs={3}, xs={9}) ----------------- */}
            <Grid item xs={3}>
              <TextField
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="postAddrNm"
                name="postAddrNm"
                label="우편번호"
                value={formik.values.postAddrNm}
                onChange={formik.handleChange}
                error={formik.touched.postAddrNm && Boolean(formik.errors.postAddrNm)}
                helperText={formik.touched.postAddrNm && formik.errors.postAddrNm}
              />
            </Grid>
            <Grid item xs={9}>
              <TextField
                required
                margin="dense"
                variant="outlined"
                fullWidth
                id="postAddrTxt"
                name="postAddrTxt"
                label="주소"
                value={formik.values.postAddrTxt}
                onChange={formik.handleChange}
                error={formik.touched.postAddrTxt && Boolean(formik.errors.postAddrTxt)}
                helperText={formik.touched.postAddrTxt && formik.errors.postAddrTxt}
              />
            </Grid>

            {/* ----------------- 네 번째 줄 (구글 캘린더 ID: 한 줄 전체 xs={12}) ----------------- */}
            <Grid item xs={12}>
              <TextField
                margin="dense"
                variant="outlined"
                fullWidth
                id="googleCalendarId"
                name="googleCalendarId"
                label="구글 캘린더 ID"
                placeholder="example@://google.com"
                value={formik.values.googleCalendarId || ''}
                onChange={formik.handleChange}
                error={formik.touched.googleCalendarId && Boolean(formik.errors.googleCalendarId)}
                helperText={formik.touched.googleCalendarId && formik.errors.googleCalendarId}
              />
            </Grid>

          </Grid>
        </Grid>
      </Grid>

      {/* 하단 저장 버튼 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3 }}>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          size="large"
          disabled={formik.isSubmitting}
          sx={{ minWidth: 100 }}
        >
          저장
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileForm;
